import { ActionIcon, Avatar, Text, Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { CopilotMessageContent } from 'app/modules/wallforming/components/copilot/CopilotMessageContext';
import { CopilotProposalCard } from 'app/modules/wallforming/components/copilot/CopilotProposalCard';
import engCopilot from 'shared/components/assets/engCopilot.jpg';
import { IS_DEV_ENV } from 'shared/constants';

import type { CopilotMessage, CopilotProposal } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface CopilotMessageItemProps {
    imageSmall?: string;
    isApplying: boolean;
    isSending: boolean;
    message: CopilotMessage;
    onApplyProposal: (proposal: CopilotProposal) => void;
    onCopyMessage: (content: string, renderedContent?: HTMLElement | null) => void;
    onRejectProposal: (proposalId: string) => void;
}

export const CopilotMessageItem = memo(function CopilotMessageItem({
    imageSmall,
    isApplying,
    isSending,
    message,
    onApplyProposal,
    onCopyMessage,
    onRejectProposal
}: CopilotMessageItemProps) {
    const { t } = useTranslation();
    const isUserMessage = message.role === 'user';

    return (
        <div className={`flex w-full ${isUserMessage ? 'justify-end' : 'justify-start'}`}>
            <div className={`flex max-w-[92%] justify-center gap-2 ${isUserMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                {isUserMessage ? (
                    <Avatar className="h-7 w-7" src={imageSmall} />
                ) : (
                    <Avatar aria-hidden="true" className="h-7 w-7 shrink-0" src={engCopilot} />
                )}

                <div className="min-w-0">
                    <Text
                        c="dimmed"
                        className={`mb-1 ${isUserMessage ? 'text-right' : 'text-left'}`}
                        fw={600}
                        size="xs"
                    >
                        {isUserMessage ? t('copilotUser') : t('copilotLabel')}
                    </Text>
                    <div
                        className={`rounded-lg border px-3 py-2 ${
                            isUserMessage
                                ? 'border-border-light bg-background-action-hover'
                                : 'border-border-light bg-background-default shadow-sm'
                        }`}
                    >
                        <div className="flex items-center gap-1">
                            <CopilotMessageContent content={message.content} role={message.role} />
                            <Tooltip label={t('copilotCopyMessage')}>
                                <ActionIcon
                                    aria-label={t('copilotCopyMessage')}
                                    className="shrink-0"
                                    size="sm"
                                    variant="subtle"
                                    onClick={(event) =>
                                        onCopyMessage(
                                            message.content,
                                            event.currentTarget.parentElement?.querySelector<HTMLElement>(
                                                '[data-copilot-message-content]'
                                            )
                                        )
                                    }
                                >
                                    <Icon aria-hidden="true" icon="content_copy" size="xs" />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                        {IS_DEV_ENV && message.tokenUsage && (
                            <Text c="dimmed" className="mt-1 text-xs italic">
                                {t('copilotTokenUsage', message.tokenUsage)}
                            </Text>
                        )}
                        {message.proposals?.map((proposal, proposalIndex) => (
                            <CopilotProposalCard
                                key={`${proposal.toolName}-${proposalIndex}`}
                                isApplying={isApplying}
                                isSending={isSending}
                                proposal={proposal}
                                onApplyProposal={onApplyProposal}
                                onRejectProposal={onRejectProposal}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});
